import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { ArrowLeftIcon, PlusIcon, TruckIcon, TrashIcon } from '@heroicons/react/24/outline';
import ConfirmationModal from '../../components/ConfirmationModal.tsx';
import api from '../../services/api.ts';
import { formatCurrency } from '../../utils/currency.ts';
import { useAuth } from '../../contexts/AuthContext.tsx';
import FrotaVehicleTabs from '../../components/frota/FrotaVehicleTabs.tsx';
import { formatPlate } from '../../utils/licensePlate.ts';

const statusChip = (status: string) => {
  if (status === 'active') return 'bg-green-100 text-green-800';
  if (status === 'maintenance') return 'bg-amber-100 text-amber-800';
  return 'bg-slate-100 text-slate-700';
};
const statusLabel = (s: string) =>
  s === 'active' ? 'Ativo' : s === 'maintenance' ? 'Em manutenção' : 'Inativo';
const kindLabel = (k: string) =>
  k === 'moto' ? 'Moto' : k === 'utilitario' ? 'Utilitário' : 'Carro';
const docChip = (status: string) => {
  if (status === 'expired') return 'bg-red-100 text-red-800';
  if (status === 'expiring') return 'bg-amber-100 text-amber-800';
  if (status === 'valid' || status === 'archived') return 'bg-slate-100 text-slate-700';
  return 'bg-green-100 text-green-800';
};
const docLabel = (status: string) => {
  if (status === 'expired') return 'Vencido';
  if (status === 'expiring') return 'A vencer';
  if (status === 'paid') return 'Pago';
  if (status === 'valid') return 'Vigente';
  if (status === 'archived') return 'Arquivado';
  return 'OK';
};
const alertBanner = (level: string) => {
  if (level === 'expired') return 'bg-red-50 border-red-200 text-red-900';
  if (level === 'expiring') return 'bg-amber-50 border-amber-200 text-amber-900';
  return 'bg-green-50 border-green-200 text-green-900';
};

const FrotaDetalhe: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data: v, isLoading, error } = useQuery(
    ['fleet-vehicle', id],
    async () => {
      const res = await api.get(`/patrimonio/frota/${id}`);
      return res.data;
    },
    { enabled: !!id }
  );

  const deleteMutation = useMutation(
    async () => {
      await api.delete(`/patrimonio/frota/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['fleet']);
        navigate('/patrimonio/frota');
      },
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (error || !v) {
    return <div className="text-center text-red-500">Veículo da frota não encontrado.</div>;
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <p className="text-sm text-gray-500">Patrimônio / Frota / {formatPlate(v.licensePlate)}</p>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center mt-1">
            <TruckIcon className="h-8 w-8 mr-3 text-primary-600" />
            {v.make} {v.model}
          </h1>
        </div>
      </div>

      <div className="toolbar" style={{ marginBottom: 4 }}>
        <div className="filters">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => navigate('/patrimonio/frota')}
          >
            ← Voltar à frota
          </button>
        </div>
        <div className="detail-actions">
          {hasPermission('assets.update') && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(`/patrimonio/frota/${id}/editar`)}
            >
              Editar
            </button>
          )}
          {hasPermission('assets.update') && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate(`/patrimonio/frota/${id}/registrar-km`)}
            >
              Registrar km
            </button>
          )}
          {hasPermission('assets.delete') && (
            <button type="button" className="btn btn-danger" onClick={() => setShowDeleteModal(true)}>
              Excluir
            </button>
          )}
        </div>
      </div>

      {id && <FrotaVehicleTabs id={id} />}

      {(v.alerts || []).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {v.alerts.map((a: any) => (
            <div key={a.id} className={`rounded-xl border px-4 py-3 text-sm ${alertBanner(a.level)}`}>
              <strong className="block">{a.title}</strong>
              <span className="opacity-80">{a.detail}</span>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Quilometragem atual</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">
            {(v.currentKm || 0).toLocaleString('pt-BR')} km
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {v.lastKmReading?.loggedAt
              ? `última leitura ${new Date(v.lastKmReading.loggedAt + 'T00:00:00').toLocaleDateString('pt-BR')}`
              : 'sem leituras'}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Utilização no mês</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">
            {(v.monthKm || 0).toLocaleString('pt-BR')} km
          </p>
          <p className="text-xs text-gray-400 mt-1">média {v.avgKmPerDay || 0} km/dia</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Custos no mês</p>
          <p className="text-2xl font-bold text-red-600 mt-1 tabular-nums">{formatCurrency(v.monthCost || 0)}</p>
          <p className="text-xs text-gray-400 mt-1">combustível + outros</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Próx. manutenção</p>
          <p className="text-xl font-bold text-gray-900 mt-1 tabular-nums">
            {v.nextPreventiveKm != null
              ? `${Number(v.nextPreventiveKm).toLocaleString('pt-BR')} km`
              : '—'}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {v.nextPreventiveRemainingKm != null
              ? `preventiva · ~${Number(v.nextPreventiveRemainingKm).toLocaleString('pt-BR')} km`
              : 'sem meta definida'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200">
          <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center">
            <h2 className="font-semibold text-gray-900">Dados do veículo</h2>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 p-5 text-sm">
            <div>
              <dt className="text-gray-500">Placa</dt>
              <dd className="font-mono font-medium">{formatPlate(v.licensePlate)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Tipo</dt>
              <dd className="font-medium">{kindLabel(v.vehicleKind)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Ano / modelo</dt>
              <dd className="font-medium">
                {v.year} / {v.model}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Condutor</dt>
              <dd className="font-medium">{v.driverName || '—'}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Valor estimado</dt>
              <dd className="font-medium">
                {v.estimatedValue != null ? formatCurrency(v.estimatedValue) : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Uso</dt>
              <dd className="font-medium">{v.usageLabel || '—'}</dd>
            </div>
          </dl>
          <div className="px-5 pb-5">
            <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-xl px-3 py-2 text-sm">
              Opcional: dívida categoria <strong>Veículo</strong> pode ser vinculada a este veículo da
              frota
              {(v.linkedDebts || []).length > 0
                ? ` · ${(v.linkedDebts as any[]).map((d: any) => d.description).join(', ')}`
                : '.'}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200">
          <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center">
            <h2 className="font-semibold text-gray-900">Documentos e seguros</h2>
            <Link to={`/patrimonio/frota/${id}/documentos`} className="text-primary-600 text-sm font-medium">
              Abrir aba Documentos →
            </Link>
          </div>
          <div className="p-5 text-sm text-gray-600 space-y-3">
            {(v.documents || []).length === 0 ? (
              <p>Nenhum documento cadastrado.</p>
            ) : (
              <>
                <p>
                  {(v.documents as any[]).length} documento(s) · gerencie na aba Documentos.
                </p>
                <ul className="space-y-2">
                  {(v.documents as any[]).slice(0, 4).map((d: any) => (
                    <li key={d.id} className="flex items-center justify-between gap-3">
                      <span className="truncate text-gray-800">{d.name}</span>
                      <span
                        className={`shrink-0 inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${docChip(
                          d.status
                        )}`}
                      >
                        {docLabel(d.status)}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200">
          <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center">
            <h2 className="font-semibold text-gray-900">
              Resumo de custos ({v.periodLabel || 'mês'})
            </h2>
            <Link to={`/patrimonio/frota/${id}/custos`} className="text-primary-600 text-sm font-medium">
              Ver todos os custos →
            </Link>
          </div>
          <dl className="p-5 space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Combustível</dt>
              <dd className="text-red-600 font-medium tabular-nums">
                {formatCurrency(v.costBreakdown?.combustivel || 0)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Manutenção</dt>
              <dd className="text-red-600 font-medium tabular-nums">
                {formatCurrency(v.costBreakdown?.manutencao || 0)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Pedágio / estacionamento</dt>
              <dd className="text-red-600 font-medium tabular-nums">
                {formatCurrency(v.costBreakdown?.pedagio || 0)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Outros</dt>
              <dd className="text-red-600 font-medium tabular-nums">
                {formatCurrency(v.costBreakdown?.outros || 0)}
              </dd>
            </div>
          </dl>
          <div className="px-5 pb-5">
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700">
              Lançamentos de custo podem gerar saída no <strong>Fluxo de caixa</strong>.
              <Link to="/financeiro/fluxo-de-caixa" className="text-primary-600 ml-2 font-medium">
                Abrir fluxo →
              </Link>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200">
          <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center">
            <h2 className="font-semibold text-gray-900">Últimas manutenções</h2>
            <Link
              to={`/patrimonio/frota/${id}/manutencoes`}
              className="text-primary-600 text-sm font-medium"
            >
              Histórico completo →
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-5 py-2 font-medium">Data</th>
                  <th className="text-left px-5 py-2 font-medium">Tipo</th>
                  <th className="text-left px-5 py-2 font-medium">Descrição</th>
                  <th className="text-right px-5 py-2 font-medium">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(v.recentMaintenances || []).length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-5 py-6 text-center text-gray-500">
                      Sem manutenções recentes.
                    </td>
                  </tr>
                ) : (
                  (v.recentMaintenances as any[]).map(m => (
                    <tr key={m.id}>
                      <td className="px-5 py-2.5 font-mono">
                        {new Date(m.serviceDate + 'T00:00:00').toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-5 py-2.5">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            m.type === 'preventive'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {m.typeLabel}
                        </span>
                      </td>
                      <td className="px-5 py-2.5">{m.description}</td>
                      <td className="px-5 py-2.5 text-right text-red-600 tabular-nums">
                        {formatCurrency(m.amount)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Excluir veículo da frota"
        message="Tem certeza que deseja excluir este veículo da frota? Esta ação não pode ser desfeita."
        onConfirm={() => {
          deleteMutation.mutate();
          setShowDeleteModal(false);
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};

export default FrotaDetalhe;
